import MoneyInput from './MoneyInput';
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth, database } from '../firebaseConfig';
import { ref, set, get, update } from 'firebase/database';
import { ORGANIZATIONS } from '../config/sites';
import { resizeImage, formatPhone, profileFromRecord, profileToSave, commissionFromRecord, commissionToSave, capAnniversaryFromDate, capAnniversaryLabel } from '../services/profileData';
import { roleLabel } from '../config/roles';
import PhoneInput from './PhoneInput';

export const USER_ROLES = [
  'Agent',
  'limited_agent',
  'TC',
  'Closing Concierge',
  'Local Assistant',
  'Marketing Manager',
  'Marketing Assistant',
];

const EMPTY = {
  firstName: '', lastName: '', email: '', role: 'Agent', organization: 'RESIDENCE',
  title: '', licenseNumber: '', googleEmail: '', phone: '', websiteUrl: '', googleBusinessUrl: '', profilePhotoUrl: '', celebrationPhotoUrl: '', anniversaryDate: '', birthdate: '', boldtrailUserId: '',
};
const EMPTY_COMMISSION = {
  capAnniversaryMonth: '', capAnniversaryDay: '', teamSplitPercent: '', brokerSplitPercent: '',
  capAmount: '', agentLeadPercent: '', teamLeadPercent: '',
};

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const styles = {
  panel: { backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '10px', padding: '28px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  title: { fontSize: '20px', fontWeight: 700, margin: '0 0 6px', color: '#1a1a1a', fontFamily: FONT },
  muted: { fontSize: '14px', color: '#7f7f7f', margin: '0 0 18px', fontFamily: FONT },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px' },
  group: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '12px', fontWeight: 700, color: '#8C6D1F', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: FONT },
  input: { padding: '12px', backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '6px', color: '#1a1a1a', fontSize: '15px', fontFamily: FONT, width: '100%', boxSizing: 'border-box' },
  inputLocked: { padding: '12px', backgroundColor: '#f5efe1', border: '1px solid #d8c9a8', borderRadius: '6px', color: '#7f7f7f', fontSize: '15px', fontFamily: FONT, width: '100%', boxSizing: 'border-box' },
  thumb: { width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37', background: '#f5efe1', display: 'block', marginBottom: 8 },
  upload: { display: 'inline-block', padding: '10px 14px', border: '1px solid #8C6D1F', color: '#8C6D1F', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: FONT },
  button: { marginTop: '8px', padding: '14px 24px', background: 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)', color: '#1a1a1a', border: '1px solid #b8903a', borderRadius: '6px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT },
  back: { background: 'none', border: 'none', color: '#8C6D1F', cursor: 'pointer', padding: 0, fontSize: '14px', marginBottom: '12px', fontFamily: FONT },
  errorBox: { backgroundColor: '#fbeeee', border: '1px solid #b00020', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#b00020', fontSize: '14px', fontFamily: FONT },
  successBox: { backgroundColor: '#eef7ee', border: '1px solid #2e7d32', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#2e7d32', fontSize: '14px', fontWeight: 600, fontFamily: FONT },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: '#1a1a1a', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid #efe6d2', paddingBottom: '8px', margin: '26px 0 16px', fontFamily: FONT },
};

// Stops waiting after a while so the button never spins forever.
const withTimeout = (promise, ms, label) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(new Error(`${label} is taking too long. Check your connection, then look in the user list before trying again.`)), ms);
  promise.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
});

// Starting values when editing an existing user.
const fromExisting = (u) => {
  const pr = profileFromRecord(u);
  return {
    form: { ...pr, googleEmail: u.googleEmail || '', email: u.email || pr.contactEmail, role: u.role || 'Agent', organization: u.organization || 'RESIDENCE', boldtrailUserId: u.boldtrailUserId != null ? String(u.boldtrailUserId) : '', emailsDisabled: u.emailsDisabled === true },
    commission: commissionFromRecord(u),
  };
};

function Field({ label, id, children }) {
  return (
    <div style={styles.group}>
      <label style={styles.label} htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

// Props:
//   existing: a user record to edit (includes uid). Leave out to add a new user.
//   onDone(message): called after an edit saves, or when the back button is pressed.
// Default for "Send welcome email now" on new users. false until launch; flip to true after launch.
export const SEND_WELCOME_EMAIL_BY_DEFAULT = false;

export default function AddUserForm({ existing = null, onDone = null }) {
  const { signup, userRole } = useAuth();
  const isAdmin = userRole === 'Admin';

  // Optional branded welcome (SendGrid). The server skips it unless SENDGRID_API_KEY is set. Never blocks adding a user.
  const sendWelcome = async (uid) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/api/welcome-email', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ uid }) });
    } catch (e) { /* optional */ }
  };
  const editing = Boolean(existing);
  const start = editing ? fromExisting(existing) : { form: EMPTY, commission: EMPTY_COMMISSION };
  const [form, setForm] = useState(start.form);
  const [commission, setCommission] = useState(start.commission);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendWelcomeNow, setSendWelcomeNow] = useState(SEND_WELCOME_EMAIL_BY_DEFAULT);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === 'phone' ? formatPhone(value) : value }));
  };

  const changeCommission = (e) => {
    const { name, value } = e.target;
    setCommission((c) => ({ ...c, [name]: value }));
  };

  const upload = async (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setError('');
      const dataUrl = await resizeImage(file);
      setForm((f) => ({ ...f, [field]: dataUrl }));
    } catch (err) {
      console.error('Image upload failed:', err);
      setError('That photo could not be loaded. Try a different image.');
    }
  };

  const profileData = () => profileToSave({ ...form, contactEmail: form.email });

  // Admin-only. Agents never see this section.
  // Limited Agents use their own plan: Broker Split (%) and Company Cap ($) from this form, cap year from the
  // anniversary date, and the lead agent's half of their deals counts as her own income (see commissionMath.js).
  const commissionData = () => ({ ...commissionToSave({ ...commission, ...capAnniversaryFromDate(form.anniversaryDate) }), customPlan: form.role === 'limited_agent' });

  // Admin-only email change: the server updates the sign-in email and users/{uid}.email, checks for duplicates, and logs it.
  const originalEmail = editing ? String(existing.email || '').trim().toLowerCase() : '';
  const emailChanged = editing && form.email.trim().toLowerCase() !== originalEmail;
  const changeEmailIfNeeded = async (uid) => {
    if (!emailChanged) return;
    if (!isAdmin) throw new Error('Only admins can change a user\'s email.');
    const idToken = await auth.currentUser.getIdToken();
    const res = await fetch('/.netlify/functions/admin-update-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ uid, email: form.email.trim() }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || 'The email could not be changed. Nothing was saved.');
  };

  const saveEdit = async () => {    const uid = existing.uid;
    await changeEmailIfNeeded(uid);
    const displayName = `${form.firstName.trim()} ${form.lastName.trim()}`;
    await withTimeout(update(ref(database, `users/${uid}`), {
      displayName,
      role: form.role,
      organization: form.organization,
      googleEmail: (form.googleEmail || '').trim().toLowerCase(),
      ...(isAdmin ? { boldtrailUserId: (form.boldtrailUserId || '').trim() || null, emailsDisabled: form.emailsDisabled === true } : {}),
    }), 15000, 'Saving the user');
    await withTimeout(set(ref(database, `users/${uid}/profile`), profileData()), 15000, 'Saving the profile');
    await withTimeout(set(ref(database, `users/${uid}/commissionSettings`), commissionData()), 15000, 'Saving commission settings');
    if (onDone) onDone(`${displayName} was updated.`);
  };

  const addNew = async () => {
    let createdUid = null;
    const email = form.email.trim().toLowerCase();
    try {
      console.log('[AddUser] checking for existing users...');
      const existingSnap = await withTimeout(get(ref(database, 'users')), 10000, 'Checking existing users');
      const all = existingSnap.val() || {};
      const dup = Object.values(all).some((u) => String(u?.email || '').trim().toLowerCase() === email);
      if (dup) {
        setError(`${form.email.trim()} is already a user. Nothing was created. Find them in the user list instead.`);
        return;
      }

      const displayName = `${form.firstName.trim()} ${form.lastName.trim()}`;
      console.log('[AddUser] creating login...');
      const newUser = await withTimeout(signup(form.email.trim(), displayName, form.role, form.organization, { sendWelcome: sendWelcomeNow }), 20000, 'Creating the login');
      createdUid = newUser.uid;
      console.log('[AddUser] login created', newUser.uid);

      await withTimeout(set(ref(database, `users/${newUser.uid}/profile`), profileData()), 15000, 'Saving the profile');
      if ((form.googleEmail || '').trim()) await withTimeout(update(ref(database, `users/${newUser.uid}`), { googleEmail: form.googleEmail.trim().toLowerCase() }), 15000, 'Saving the Google email');
      if (isAdmin && (form.boldtrailUserId || '').trim()) await withTimeout(update(ref(database, `users/${newUser.uid}`), { boldtrailUserId: form.boldtrailUserId.trim() }), 15000, 'Saving the BoldTrail agent number');
      await withTimeout(set(ref(database, `users/${newUser.uid}/commissionSettings`), commissionData()), 15000, 'Saving commission settings');
      console.log('[AddUser] done');

      setSuccess(newUser.inviteSkipped
        ? `${displayName} added with the temporary password welcome. No email was sent. They sign in at resisystem.com/login and set their own password.`
        : newUser.inviteSent
        ? `${displayName} added. A welcome email was sent to ${form.email.trim()}: sign in at resisystem.com/login with the temporary password welcome.`
        : `${displayName} added with the temporary password welcome, but the email could not be sent. Text them: sign in at resisystem.com/login with ${form.email.trim()} and welcome.`);
      setForm(EMPTY);
      setCommission(EMPTY_COMMISSION);
      setSendWelcomeNow(SEND_WELCOME_EMAIL_BY_DEFAULT);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Add user failed:', err);
      if (err?.code === 'auth/email-already-in-use') {
        setError(`${form.email.trim()} already has a login. Nothing new was created. Find them in the user list instead.`);
      } else if (createdUid) {
        setError(`The login for ${form.email.trim()} was created, but the rest of the profile did not save (${err.message}). Do not add them again. Open their profile from the user list to finish.`);
      } else {
        setError(err.message || 'Failed to create user');
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('Please fill in first name, last name, and email.');
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await saveEdit();
      } else {
        await addNew();
      }
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.message || 'Could not save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div style={styles.panel}>
        {onDone && (
          <button type="button" onClick={() => onDone()} style={styles.back}>← Back to users</button>
        )}
        <h2 style={styles.title}>{editing ? 'Edit User' : 'Add New User'}</h2>
        <p style={styles.muted}>
          {editing
            ? (isAdmin ? 'Changes save to the user record. Changing the email also changes the address they sign in with.' : 'Changes save to the user record. Only an admin can change the email, because it is the login.')
            : 'Creates the login and the profile. Anything in the commission section is visible only to admins.'}
        </p>
        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        <div style={styles.sectionTitle}>Account</div>
        <div style={styles.grid}>
          <Field label="First Name" id="firstName">
            <input id="firstName" name="firstName" value={form.firstName} onChange={change} style={styles.input} />
          </Field>
          <Field label="Last Name" id="lastName">
            <input id="lastName" name="lastName" value={form.lastName} onChange={change} style={styles.input} />
          </Field>
          <Field label="Email Address" id="email">
            <input id="email" name="email" type="email" value={form.email} onChange={change} disabled={editing && !isAdmin} style={editing && !isAdmin ? styles.inputLocked : styles.input} />
          </Field>
          <Field label="Role" id="role">
            <select id="role" name="role" value={form.role} onChange={change} style={styles.input}>
              {USER_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </Field>
          {editing && isAdmin && (
            <label htmlFor="emailsDisabled" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
              <input id="emailsDisabled" type="checkbox" checked={form.emailsDisabled === true} onChange={(e) => setForm((f) => ({ ...f, emailsDisabled: e.target.checked }))} />
              Block all emails (Resi never emails, CCs or replies-to this person)
            </label>
          )}
          <Field label="Organization" id="organization">
            <select id="organization" name="organization" value={form.organization} onChange={change} style={styles.input}>
              {ORGANIZATIONS.map((org) => (
                <option key={org} value={org}>{org === 'RESIDENCE' ? 'RESIDENCE | eXp Realty' : 'RISE'}</option>
              ))}
            </select>
          </Field>
          {isAdmin && (
            <Field label="BoldTrail Agent Number" id="boldtrailUserId">
              <input id="boldtrailUserId" name="boldtrailUserId" inputMode="numeric" value={form.boldtrailUserId || ''} onChange={change} placeholder="e.g. 1233031" style={styles.input} />
            </Field>
          )}
        </div>

        <div style={styles.sectionTitle}>Profile</div>
        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            {form.profilePhotoUrl ? <img src={form.profilePhotoUrl} alt="Headshot" style={styles.thumb} /> : <div style={styles.thumb} />}
            <div style={styles.label}>Headshot</div>
            <label style={{ ...styles.upload, marginTop: 8 }}>
              Upload headshot
              <input type="file" accept="image/*" onChange={(e) => upload(e, 'profilePhotoUrl')} style={{ display: 'none' }} />
            </label>
          </div>
          <div>
            {form.celebrationPhotoUrl ? <img src={form.celebrationPhotoUrl} alt="Celebration headshot" style={styles.thumb} /> : <div style={styles.thumb} />}
            <div style={styles.label}>Celebration headshot</div>
            <label style={{ ...styles.upload, marginTop: 8 }}>
              Upload celebration photo
              <input type="file" accept="image/*" onChange={(e) => upload(e, 'celebrationPhotoUrl')} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
        <div style={styles.grid}>
          <Field label="Title" id="title">
            <input id="title" name="title" value={form.title} onChange={change} placeholder="e.g., Realtor®, Team Leader" style={styles.input} />
          </Field>
          <Field label="Phone" id="phone">
            <PhoneInput id="phone" name="phone" value={form.phone} onChange={change} placeholder="(239) 555-1234" style={styles.input} />
          </Field>
          <Field label="Google Email (admin only)" id="googleEmail">
            <input id="googleEmail" name="googleEmail" type="email" value={form.googleEmail || ''} onChange={change} placeholder="Their Google account for Calendar" style={styles.input} />
          </Field>
          <Field label="Website Link" id="websiteUrl">
            <input id="websiteUrl" name="websiteUrl" value={form.websiteUrl} onChange={change} placeholder="https://..." style={styles.input} />
          </Field>
          {form.role !== 'limited_agent' && (
          <Field label="Google Business Profile Link" id="googleBusinessUrl">
            <input id="googleBusinessUrl" name="googleBusinessUrl" value={form.googleBusinessUrl} onChange={change} placeholder="https://maps.google.com/..." style={styles.input} />
          </Field>
          )}
          <Field label="Realtor Anniversary Date" id="anniversaryDate">
            <input id="anniversaryDate" name="anniversaryDate" type="date" value={form.anniversaryDate} onChange={change} style={styles.input} />
          </Field>
          <Field label="Birthdate" id="birthdate">
            <input id="birthdate" name="birthdate" type="date" value={form.birthdate} onChange={change} style={styles.input} />
          </Field>
        </div>

        <div style={styles.sectionTitle}>Commission and cap (admin only)</div>
        <div style={styles.grid}>
          <Field label="Cap Anniversary (calculated)" id="capAnniversary">
            <div style={styles.inputLocked}>{capAnniversaryLabel(form.anniversaryDate) || 'Enter the Realtor anniversary date above'}</div>
          </Field>
          <Field label="Agent-Generated Lead Commission (%)" id="agentLeadPercent">
            <input id="agentLeadPercent" name="agentLeadPercent" type="number" min="0" max="100" step="0.1" value={commission.agentLeadPercent} onChange={changeCommission} style={styles.input} />
          </Field>
          <Field label="Team-Generated Lead Commission (%)" id="teamLeadPercent">
            <input id="teamLeadPercent" name="teamLeadPercent" type="number" min="0" max="100" step="0.1" value={commission.teamLeadPercent} onChange={changeCommission} style={styles.input} />
          </Field>
          <Field label="Broker Split (%)" id="brokerSplitPercent">
            <input id="brokerSplitPercent" name="brokerSplitPercent" type="number" min="0" max="100" step="0.1" value={commission.brokerSplitPercent} onChange={changeCommission} style={styles.input} />
          </Field>
          <Field label="Company Cap ($)" id="capAmount">
            <MoneyInput id="capAmount" name="capAmount" min="0" value={commission.capAmount} onChange={changeCommission} style={styles.input} />
          </Field>
        </div>

        {!editing && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0 12px', fontSize: 14 }}>
            <input type="checkbox" checked={sendWelcomeNow} onChange={(e) => setSendWelcomeNow(e.target.checked)} />
            Send welcome email now
            <span style={{ color: '#7f7f7f', fontSize: 12 }}>(otherwise send it later with Send welcome email in the user list)</span>
          </label>
        )}
        <button type="submit" disabled={loading} style={{ ...styles.button, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Saving...' : editing ? 'Save Changes' : 'Create User'}
        </button>
      </div>
    </form>
  );
}
